/**
 * tamriel.js — Tamriel time engine constants.
 *
 * Pure data; no side effects, no DOM access.
 *
 * ESO timescale based on UESP ESO Clock (esoclock.uesp.net) calibration:
 *   1 game day = 20955 real seconds (~5.82 real hours, ~349 real minutes)
 *   1 game hour = 873.125 real seconds (~14.55 real minutes)
 *   Epoch: Apr 4 2014 00:00:00 UTC (ESO PC launch) with calibration offset so
 *          launch day corresponds to 4th Rain's Hand 2E 582 in-game.
 */

export const TAMRIEL_GAMETIME_OFFSET      = 1396569600;   // Apr 4, 2014 00:00:00 UTC (ESO PC launch)
export const TAMRIEL_GAMETIME_CALIBRATION = -1909707;     // calibration: launch day = 4th Rain's Hand
export const TAMRIEL_EPOCH_SEC            = TAMRIEL_GAMETIME_OFFSET + TAMRIEL_GAMETIME_CALIBRATION;
export const TAMRIEL_SECS_PER_GAMEDAY     = 20955;        // real seconds per game day (~5.82 hrs)
export const TAMRIEL_SECS_PER_GAMEYEAR    = TAMRIEL_SECS_PER_GAMEDAY * 365; // 365-day game year

export const TAMRIEL_MONTHS = [
  'Morning Star', "Sun's Dawn", 'First Seed', "Rain's Hand",
  'Second Seed', 'Midyear', "Sun's Height", 'Last Seed',
  'Hearthfire', 'Frostfall', "Sun's Dusk", 'Evening Star'
];
export const TAMRIEL_DAYS_IN_MONTH = 30; // 30 days per month, 12 months (360 days + 5 overflow)

// --- Lunar cycle ------------------------------------------------------------
// In ESO, Jode (Masser) and Jone (Secunda) share a single synchronised cycle
// of ~30 Tamriel days (~628,650 real seconds ≈ 7.28 real days). This matches
// the "Tamriel Standard Time" / "Moons" addons used in-game.
export const LUNAR_CYCLE_DAYS = 30;
export const LUNAR_CYCLE_SEC  = LUNAR_CYCLE_DAYS * TAMRIEL_SECS_PER_GAMEDAY; // 628,650
// Reference Unix second at which both moons are FULL. Calibrated from an
// in-game observation (Moons addon: 53% waxing, ~34 real hours to full, taken
// on 2026-04-24 ≈ 12:00 UTC → full moon at Unix 1777788000). If the site
// drifts from the addon over time, nudge this constant by a few thousand
// seconds to re-align. Any verified full-moon moment works.
export const LUNAR_FULL_EPOCH_SEC = 1777788000;

// --- Lore-accurate lunar cycles (informational only) ----------------------
// ESO renders Jode and Jone as a single synced cycle (see above), but the
// books and earlier Elder Scrolls games describe them as separate bodies:
// Jode (Masser) is larger and slower, Jone (Secunda) is smaller and faster,
// cycling roughly twice for every full Jode cycle (see src/ai/intent.js).
// These values drive the "lore" line in the tooltip only — the visual moons
// stay engine-accurate so the site keeps matching in-game addons.
export const LORE_JODE_CYCLE_SEC = 30 * TAMRIEL_SECS_PER_GAMEDAY; // Masser ~30 days
export const LORE_JONE_CYCLE_SEC = 15 * TAMRIEL_SECS_PER_GAMEDAY; // Secunda ~15 days

// Time-of-day states matching RAG/roleplay_timeOfDay.json
export const TOD_STATES = {
  late_night:    { hours: [0, 5],   label: 'Late Night',    hue: 220, sat: 0.6,  bright: 0.75, glowR: 100, glowG: 120, glowB: 200 },
  early_morning: { hours: [5, 9],   label: 'Early Morning', hue: 35,  sat: 0.7,  bright: 0.88, glowR: 200, glowG: 180, glowB: 100 },
  midday:        { hours: [9, 17],  label: 'Midday',        hue: 140, sat: 0,    bright: 1.0,  glowR: 74,  glowG: 222, glowB: 128 },
  evening:       { hours: [17, 21], label: 'Evening',       hue: 30,  sat: 0.5,  bright: 0.88, glowR: 200, glowG: 160, glowB: 80  },
  night:         { hours: [21, 24], label: 'Night',         hue: 230, sat: 0.5,  bright: 0.78, glowR: 90,  glowG: 110, glowB: 190 },
};

export const TOD_COMMENTARY = {
  late_night: [
    "Fissal is hooked to the charging bay in the Dwemer section, optics dimmed. Moonsugar Meadow is hushed save for the chirp of crickets.",
    "The guild hall sleeps. Distant scuttlers clink in the Tree Cave. Jone and Jode cast silver ribbons across the meadow.",
  ],
  early_morning: [
    "Dawn breaks over Moonsugar Meadow. Fissal is running morning diagnostics, calculating overnight sales with a cheerful whirr.",
    "Fresh morning light floods the guild hall. This one smells breakfast wafting from the mansion kitchen. Time to open the merchant booth!",
  ],
  midday: [
    "The sun is high over Moonsugar Meadow. Guild members bustle about. Fissal's optics are bright and focused on today's trading data.",
    "Peak trading hours! The kiosks are lively. Fissal cross-references prices with mechanical precision.",
  ],
  evening: [
    "Sunset paints Moonsugar Meadow in amber and rose. Fissal's lenses catch the fading light as the guild winds down for the day.",
    "Tavern music drifts from afar. Lamps flicker to life across the guild hall. This one reflects on the day's trades.",
  ],
  night: [
    "Stars blanket the sky above Moonsugar Meadow. Fissal's optics glow softly cyan in the dark, reviewing the day's final tallies.",
    "The moons hang low. Quieter now. Fissal contemplates the celestial machinery above, gears turning slower.",
  ],
};
